import { useIsMobile } from "@/hooks/use-mobile";
import {
  getRequiredFields,
  removeEmptyFields,
  setEmptyFieldsToNull,
  toYMD,
} from "@/lib/utils";
import { useCreateCrmClassMutation } from "@/services/school";
import { useGetCurriculumGrades } from "@/services/masterData/useGetCurriculumGrades";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import { useForm, useFieldArray } from "react-hook-form";
import { useParams } from "react-router-dom";
import {
  GRADE_NAME,
  NO_OF_STUDENT,
  SECTION,
  SECTION_NAME,
  YEAR,
} from "./consts";
import { formValues, schema } from "./validations";

interface UseUpdateAmountProps {
  onCancel: () => void;
  editData?: any;
}

const useUpdateCurriculum = ({ onCancel, editData }: UseUpdateAmountProps) => {
  const isMobile = useIsMobile();
  const { action, id, installmentIndex } = useParams();

  // Fetch curriculum grades data
  const { data: gradeData, isLoading: isGradeDataLoading } =
    useGetCurriculumGrades();

  const gradeOptions =
    gradeData?.data?.map((g: any) => ({
      label: g?.curriculum_grade,
      value: String(g?.curriculum_id),
    })) || [];

  const form = useForm<formValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      [GRADE_NAME]: "",
      [YEAR]: "",
      [SECTION]: Array.from({ length: 1 }, () => ({
        _id: "",
        [SECTION_NAME]: "",
        [NO_OF_STUDENT]: "",
      })),
    },
  });

  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: SECTION,
  });

  const requiredFields = getRequiredFields(schema);
  const createCrmClass = useCreateCrmClassMutation({
    onSuccess: () => {
      // Reset form before closing
      form.reset({
        [GRADE_NAME]: "",
        [YEAR]: "",
        [SECTION]: Array.from({ length: 1 }, () => ({
          _id: "",
          [SECTION_NAME]: "",
          [NO_OF_STUDENT]: "",
        })),
      });
      onCancel();
    },
    onError: (error) => {
      console.error("Error creating CRM class:", error);
    },
  });

  useEffect(() => {
    // if (editData) {
    //   form.reset({
    //
    //   });
    // } else {
    //   form.reset({
    //     [PAID_AMOUNT]: "",
    //     [PAID_AMOUNT]: "",
    //     [PAID_AMOUNT]: "",
    //     [PAID_AMOUNT]: "",
    //     [PAID_AMOUNT]: "",
    //     [PAID_AMOUNT]: "",
    //     [PAID_AMOUNT]: "",
    //   });
    // }
  }, []);

  const onAddMoreSection = () => {
    append({
      _id: "",
      [SECTION_NAME]: "",
      [NO_OF_STUDENT]: "",
    });
  };

  const onRemoveSection = (index: number) => {
    if (fields.length > 1) {
      remove(index);
    }
  };

  const handleCancel = () => {
    // Reset form to default values
    form.reset({
      [GRADE_NAME]: "",
      [YEAR]: "",
      [SECTION]: Array.from({ length: 1 }, () => ({
        _id: "",
        [SECTION_NAME]: "",
        [NO_OF_STUDENT]: "",
      })),
    });
    onCancel();
  };

  const onSubmit = (data: formValues) => {
    let _data = removeEmptyFields(data);

    // Transform data to match API requirements
    const apiData = {
      schoolId: String(id),
      curriculumId: data[GRADE_NAME],
      year: data[YEAR],
      section:
        data[SECTION]?.map((section: any) => ({
          name: section[SECTION_NAME],
          noStudent: parseInt(section[NO_OF_STUDENT]) || 0,
        })) || [],
    };

    createCrmClass.mutate(apiData);
  };

  return {
    form,
    onSubmit,
    requiredFields,
    isMobile,
    gradeOptions,
    isLoading: createCrmClass.isPending,
    isGradeDataLoading,
    fields,
    onAddMoreSection,
    onRemoveSection,
    handleCancel,
  };
};

export default useUpdateCurriculum;
